import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import chroma from 'chroma-js';

const GravityMap = forwardRef(({ data, onHover, colorScale = 'Default (Red-Blue)' }, ref) => {
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const mapContainerRef = useRef(null);

  // Color scales mapping
  const colorScales = {
    'Default (Red-Blue)': ['#b2182b', '#f7f7f7', '#2166ac'],
    'Viridis': ['#440154', '#21918c', '#fde725'],
    'Plasma': ['#0d0887', '#9c179e', '#ed7953'],
    'Inferno': ['#000004', '#b40426', '#f0f921'],
    'Magma': ['#000004', '#b5367a', '#fcfdbf'],
    'Cividis': ['#00204d', '#7c7b78', '#ffe100']
  };

  const getColorScale = () => {
    const colors = colorScales[colorScale] || colorScales['Default (Red-Blue)'];
    return chroma.scale(colors).domain([-50, 0, 50]);
  };

  useImperativeHandle(ref, () => ({
    flyToBounds: (bounds, options) => {
      if (mapRef.current) {
        mapRef.current.flyToBounds(bounds, options);
      }
    },
    flyTo: (center, zoom, options) => {
      if (mapRef.current) {
        mapRef.current.flyTo(center, zoom, options);
      }
    },
    getMap: () => mapRef.current
  }));

  useEffect(() => {
    if (!mapRef.current && mapContainerRef.current) {
      // Initialize map with scrollWheelZoom disabled to prevent page scrolling issues
      mapRef.current = L.map(mapContainerRef.current, {
        scrollWheelZoom: false, // Disable scroll zoom to prevent conflict with page scrolling
        zoomControl: true,       // Keep zoom controls
        dragging: true,          // Allow dragging
        touchZoom: true,         // Allow touch zoom
        doubleClickZoom: true,   // Allow double click zoom
        boxZoom: true            // Allow box zoom
      }).setView([20, 0], 2);
      
      // Add zoom control to top-right (default position)
      mapRef.current.zoomControl.setPosition('topleft');

      // Add base map tiles
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      }).addTo(mapRef.current);

      // Add a message that scrolling is disabled for zoom
      const scrollMessage = L.control({ position: 'bottomright' });
      scrollMessage.onAdd = function() {
        const div = L.DomUtil.create('div', 'scroll-message');
        div.innerHTML = '🔍 Use + / - buttons to zoom';
        div.style.backgroundColor = 'white';
        div.style.padding = '5px 10px';
        div.style.borderRadius = '4px';
        div.style.boxShadow = '0 2px 5px rgba(0,0,0,0.2)';
        div.style.fontSize = '12px';
        div.style.margin = '10px';
        return div;
      };
      scrollMessage.addTo(mapRef.current);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !data || data.length === 0) return;

    if (layerRef.current) {
      mapRef.current.removeLayer(layerRef.current);
    }

    layerRef.current = L.layerGroup().addTo(mapRef.current);
    const colorScaleFn = getColorScale();

    data.forEach(point => {
      if (point.value !== null && !isNaN(point.value)) {
        const color = colorScaleFn(point.value).hex();
        
        const rect = L.rectangle(
          [[point.lat - 1, point.lng - 1], [point.lat + 1, point.lng + 1]],
          {
            color: color,
            weight: 0,
            fillColor: color,
            fillOpacity: 0.7,
            interactive: true
          }
        );

        rect.on('mouseover', () => {
          onHover(point);
          rect.setStyle({ fillOpacity: 0.9, weight: 1, color: '#333' });
        });

        rect.on('mouseout', () => {
          onHover(null);
          rect.setStyle({ fillOpacity: 0.7, weight: 0 });
        });

        rect.addTo(layerRef.current);
      }
    });
  }, [data, onHover, colorScale]);

  return <div id="map" ref={mapContainerRef} style={{ height: '600px', width: '100%' }} />;
});

export default GravityMap;